<script>
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { activeDomain } from '@client/stores/domain-settings';
  import { reassignField, resetField, selectedUser } from '@client/stores/virtual-background';

  import { generateUUID } from '@common/utils/strings';

  import FieldInput from './field-input.svelte';

  /**
   * The assigned field.
   * @type {import('@client/stores/types').AssignedField}
   */
  export let field;

  /**
   * If true, this field has been modified in the editor, allows reset.
   * @type {boolean}
   */
  export let modified;

  /**
   * If true, this field is not available in the domain.
   * @type {boolean}
   */
  export let unavailable = false;

  /**
   * If true, this field is not set for the current user.
   * @type {boolean}
   */
  export let unset = false;

  /** @type {string} */
  let id = `assigned-${generateUUID()}`;

  /** @type {HTMLInputElement} */
  let input;

  /**
   * @type {'replace' | 'default' | undefined}
   */
  let mode;

  /**
   * Sets a new value for the field.
   * @param {import('@client/stores/types').ValueOrField} newValue - The new value or field data.
   */
  const setValue = (newValue) => {
    field = { ...field, ...newValue };
    reassignField({ ...field, ...newValue });

    mode = undefined;
  };

  /**
   * Sets a new default value for the field.
   * @param {import('@client/stores/types').ValueOrField} newDefault - The new default value or field data.
   */
  const setDefaultValue = (newDefault) => {
    field = { ...field, default: { ...newDefault } };
    reassignField({ ...field, default: { ...newDefault } });

    mode = undefined;
  };

  const cancel = () => {
    mode = undefined;
  };

  const reset = () => {
    resetField(field.name);
  };

  $: if (input) {
    input.focus();
    input.select();
  }
</script>

<div class="align-items-center border-bottom {mode ? 'py-0' : 'd-flex py-2'}">
  {#if mode === 'default'}
    <FieldInput
      name={field.name}
      placeholder={$format('label.PLACEHOLDER.FIELD_DEFAULT')}
      value={field.default.value || field.default.field}
      onChange={setDefaultValue}
      onCancel={cancel}
    />
  {:else if mode === 'replace'}
    <FieldInput
      name={field.name}
      placeholder={$format('label.PLACEHOLDER.FIELD_REPLACE')}
      value={field.value || field.field}
      onChange={setValue}
      onCancel={cancel}
    />
  {:else}
    <Dropdown>
      <DropdownToggle color="link" size="sm" class="text-start p-0" disabled={!!mode}>
        <Icon id="{id}-edit" name="edit" class="me-2 text-secondary" />

        <div class="d-inline-flex">
          <small id="{id}-value" class:text-body={!unavailable} class:text-danger={unavailable}>
            {#if field.field !== field.name && (field.field || field.value)}
              <s>{field.name}</s>
              <Icon name="arrow-right" style="font-size: .75rem" />
              {field.field || field.value || field.name}
            {:else}
              {field.name}
            {/if}
            {#if field.default?.field || field.default?.value}
              <sub class="text-muted d-block">
                <Icon name="corner-down-right" />
                {field.default.field || field.default.value}
              </sub>
            {/if}
          </small>
        </div>
      </DropdownToggle>
      <Tooltip target="{id}-edit">
        <small>{$format('label.TOOLTIP.FIELD_CHANGE')}</small>
      </Tooltip>

      <DropdownMenu>
        <DropdownItem class="py-2 px-3" on:click={() => (mode = 'default')}>
          <small>
            <Icon name="corner-down-right" />
            {$format('label.FIELD_DEFAULT')}
          </small>
        </DropdownItem>
        {#if modified}
          <div class="dropdown-divider" />
          <DropdownItem class="py-2 px-3 text-primary" on:click={reset}>
            <small>
              <Icon name="undo-2" />
              {$format('label.FIELD_REVERT')}
            </small>
          </DropdownItem>
        {/if}
      </DropdownMenu>
    </Dropdown>

    <div class="ms-auto me-1 d-flex align-items-center">
      {#if unavailable}
        <Icon name="alert-triangle" class="text-danger" id="{id}-unavailable" style="font-size: .75rem" />
        <Tooltip target="{id}-unavailable" placement="left">
          <small>
            {@html $format('label.TOOLTIP.FIELD_UNAVAILABLE', { values: { name: field.name, domain: $activeDomain } })}
          </small>
        </Tooltip>
      {:else if unset}
        <Icon name="info" class="text-primary" id="{id}-unset" style="font-size: .75rem" />
        <Tooltip target="{id}-unset" placement="left">
          <small>
            {@html $format('label.TOOLTIP.FIELD_UNSET', { values: { name: field.name, email: $selectedUser?.email } })}
          </small>
        </Tooltip>
      {/if}
    </div>
  {/if}
</div>
